import { cssNotoCjk } from './letras-docs';

/**
 * Letra e interlineado del editor de Revisión (TinyMCE): qué fuentes se
 * ofrecen, cómo se cargan en el editor, el botón «Aplicar a todo el
 * documento» y el menú de interlineado.
 *
 * Regla de Andrey (7 oct 2026): solo letras que se vean bien A LA VEZ en
 * Google Docs y en Word (Windows y Mac), y por tanto en el editor, el PDF y la
 * vista previa. Dos grupos:
 *
 * 1. Las de Office (FUENTES_OFFICE). Salen IGUAL en los tres sitios porque:
 *  - PDF: lo pinta Chrome en processing-ms, que no tiene fuentes de Microsoft;
 *    fontconfig sirve bajo el nombre de Office su gemela métrica instalada
 *    (Liberation → Arial/Arial Narrow/Times New Roman/Courier New, Carlito →
 *    Calibri, Gelasio → Georgia, Nimbus Sans → Helvetica).
 *  - Word: el DOCX declara la PRIMERA familia de la pila y Word la resuelve en
 *    la máquina del usuario, así que la primera tiene que ser el nombre de Office.
 *  - Editor: carga esas mismas libres (public/fonts) bajo el nombre de Office.
 *  Gelasio va con las métricas verticales de Georgia parcheadas (la original
 *  tiene la línea natural un 12 % más alta) y es el mismo archivo que instala
 *  processing-ms. Cambria es la excepción: Caladea no tiene sus anchos y el Word
 *  sale un 3-5 % más ancho que el editor y el PDF, pero se sigue ofreciendo por
 *  decisión de Andrey (7 oct 2026). Helvetica no está en Google Docs (la pinta
 *  en Arial) y Arial Narrow es dudosa (solo sale en el enum antiguo de Apps
 *  Script, no en Google Fonts): las dos esperan su decisión.
 *
 * 2. Las libres de Google Fonts (FUENTES_LIBRES, OFL-1.1). Docs las ofrece por
 *  su nombre en «Más fuentes»; Word no las trae, así que el .docx las lleva
 *  incrustadas (solo las usadas); processing-ms y OnlyOffice instalan los
 *  mismos archivos. El editor las carga en WOFF2 (conversión sin pérdidas de
 *  los TTF oficiales) desde public/fonts/libres/<Familia>/, con su OFL.txt al
 *  lado. Solo se descargan si el texto las usa (font-display: swap).
 *
 * Garamond, Verdana, Tahoma… no tienen gemela métrica libre: el PDF las cambia
 * por otra letra y los renglones no cuadran con lo revisado. Un documento que
 * ya usa una de ellas se sigue viendo con ella; solo deja de ofrecerse.
 *
 * Archivo sin React para poder probarlo con vitest.
 */

import { BOTON_MARGENES } from './margenes-hoja';

/** El botón «Paginación» de la traducción (lib/orders/paginacion-hojas.ts). */
export const BOTON_PAGINACION = 'ezpaginacion';

/**
 * Regular, negrita, cursiva y negrita cursiva, en ese orden. Las manuscritas
 * pueden traer solo la regular, o regular y negrita: el estilo que falta lo
 * simula el navegador, como Word y Google Docs.
 */
export type ArchivosFuente =
  | readonly [string]
  | readonly [string, string]
  | readonly [string, string, string, string];

/** Grupo del selector: el de Office y las cuatro clases de las libres. */
export type GrupoFuente = 'office' | 'serifa' | 'sans' | 'mono' | 'manuscrita';

export interface FuenteEditor {
  /** Nombre en el selector (y el que declara el Word). */
  titulo: string;
  /** Valor de font-family que se escribe en el HTML. */
  pila: string;
  grupo: GrupoFuente;
  /** Archivos de public/fonts que el editor carga bajo `titulo`; sin ellos usa la del sistema. */
  web?: ArchivosFuente;
}

const ESTILOS_HABITUALES = ['Regular', 'Bold', 'Italic', 'BoldItalic'] as const;

/** Los cuatro archivos de una familia: serie('Gelasio') → Gelasio-Regular.ttf, Gelasio-Bold.ttf… */
function serie(base: string, extension: 'ttf' | 'otf' = 'ttf', estilos: readonly [string, string, string, string] = ESTILOS_HABITUALES): ArchivosFuente {
  const [r, n, c, nc] = estilos.map((e) => `${base}-${e}.${extension}`);
  return [r, n, c, nc];
}

// Un nombre de familia sin comillas solo es CSS válido si cada palabra es un
// identificador: «Source Serif 4» sin comillas invalida la declaración entera.
const IDENTIFICADOR_CSS = /^-?[A-Za-z_][A-Za-z0-9_-]*$/;

/**
 * Nombre de familia listo para CSS: tal cual si cada palabra es un
 * identificador (Arial, Times New Roman, EB Garamond) y entre comillas
 * SIMPLES si no ('Source Serif 4'). Nunca dobles: dentro de un atributo
 * style partían el atributo y cortaban el Word.
 */
export function nombreCss(nombre: string): string {
  const limpio = String(nombre ?? '').replace(/["']/g, '').trim().replace(/\s+/g, ' ');
  return !limpio || limpio.split(' ').every((p) => IDENTIFICADOR_CSS.test(p)) ? limpio : `'${limpio}'`;
}

/**
 * Pila de font-family válida a partir de lo que traiga el HTML: comillas de
 * cualquier tipo (también como entidad: &#39;, &apos;, &quot;) fuera, y
 * nombreCss en cada familia. «Source Serif 4, serif» → «'Source Serif 4', serif».
 */
export function pilaCss(pila: string): string {
  return String(pila ?? '')
    .replace(/&#0*39;|&#x0*27;|&apos;|&quot;|&#0*34;|&#x0*22;/gi, "'")
    .split(',')
    .map((f) => nombreCss(f))
    .filter(Boolean)
    .join(', ');
}

const GENERICA: Record<Exclude<GrupoFuente, 'office'>, string> = { serifa: 'serif', sans: 'sans-serif', mono: 'monospace', manuscrita: 'cursive' };

/**
 * Una familia libre: libre('Source Serif 4', 'serifa') → pila «'Source Serif 4',serif»
 * y archivos libres/SourceSerif4/SourceSerif4-Regular.woff2…
 */
function libre(titulo: string, grupo: Exclude<GrupoFuente, 'office'>, estilos: 'r' | 'rn' | 'rnci' = 'rnci'): FuenteEditor {
  const carpeta = titulo.replace(/\s+/g, '');
  const archivo = (e: string) => `libres/${carpeta}/${carpeta}-${e}.woff2`;
  const web: ArchivosFuente = estilos === 'r'
    ? [archivo('Regular')]
    : estilos === 'rn'
      ? [archivo('Regular'), archivo('Bold')]
      : [archivo('Regular'), archivo('Bold'), archivo('Italic'), archivo('BoldItalic')];
  return { titulo, grupo, pila: `${nombreCss(titulo)},${GENERICA[grupo]}`, web };
}


export const FUENTES_OFFICE: readonly FuenteEditor[] = [
  { titulo: 'Arial', grupo: 'office', pila: 'Arial,Helvetica,sans-serif', web: serie('LiberationSans') },
  { titulo: 'Arial Narrow', grupo: 'office', pila: 'Arial Narrow,Liberation Sans Narrow,sans-serif', web: serie('LiberationSansNarrow') },
  { titulo: 'Calibri', grupo: 'office', pila: 'Calibri,Carlito,sans-serif', web: serie('Carlito') },
  { titulo: 'Cambria', grupo: 'office', pila: 'Cambria,Caladea,serif', web: serie('Caladea') },
  { titulo: 'Courier New', grupo: 'office', pila: 'Courier New,Courier,monospace' },
  { titulo: 'Georgia', grupo: 'office', pila: 'Georgia,Gelasio,serif', web: serie('Gelasio') },
  { titulo: 'Helvetica', grupo: 'office', pila: 'Helvetica,Arial,sans-serif' },
  { titulo: 'Times New Roman', grupo: 'office', pila: 'Times New Roman,Times,serif', web: serie('LiberationSerif') },
  // Pendientes de decisión (clones URW, licencia AGPL-3 con excepción de
  // fuentes): cada una es una línea aquí, sus cuatro archivos y su licencia en
  // public/fonts, y sus alias en processing-ms y OnlyOffice.
  //   { titulo: 'Book Antiqua', grupo: 'office', pila: 'Book Antiqua,P052,Palatino,serif', web: serie('P052', 'otf', ['Roman', 'Bold', 'Italic', 'BoldItalic']) },
  //   { titulo: 'Bookman Old Style', grupo: 'office', pila: 'Bookman Old Style,URW Bookman,serif', web: serie('URWBookman', 'otf', ['Light', 'Demi', 'LightItalic', 'DemiItalic']) },
  //   { titulo: 'Century Gothic', grupo: 'office', pila: 'Century Gothic,URW Gothic,sans-serif', web: serie('URWGothic', 'otf', ['Book', 'Demi', 'BookOblique', 'DemiOblique']) },
  //   { titulo: 'Century Schoolbook', grupo: 'office', pila: 'Century Schoolbook,C059,serif', web: serie('C059', 'otf', ['Roman', 'Bold', 'Italic', 'BdIta']) },
];

/**
 * Libres de Google Fonts, por clase. Interlineado: Word mide el renglón natural
 * de cada letra con sus métricas de Windows y el navegador con las otras; en
 * Lora, Libre Baskerville, Crimson Pro, Libre Franklin, Poppins, Courier Prime
 * y Dancing Script el de Word sale un 14-18 % más alto, y en Pinyon Script,
 * Montserrat y Great Vibes un 24-40 %. El interlineado elegido en el editor
 * (data-ez-interlineado) lo tiene que compensar el .docx por familia. Great
 * Vibes y Pinyon Script son para firmas de un renglón.
 */
export const FUENTES_LIBRES: readonly FuenteEditor[] = [
  libre('Lora', 'serifa'),
  libre('EB Garamond', 'serifa'),
  libre('Libre Baskerville', 'serifa'),
  libre('Source Serif 4', 'serifa'),
  libre('Noto Serif', 'serifa'),
  libre('PT Serif', 'serifa'),
  libre('Spectral', 'serifa'),
  libre('Crimson Pro', 'serifa'),
  libre('Roboto Serif', 'serifa'),
  libre('IBM Plex Serif', 'serifa'),
  libre('Roboto', 'sans'),
  libre('Open Sans', 'sans'),
  libre('Montserrat', 'sans'),
  libre('Source Sans 3', 'sans'),
  libre('PT Sans', 'sans'),
  libre('IBM Plex Sans', 'sans'),
  libre('Fira Sans', 'sans'),
  libre('Libre Franklin', 'sans'),
  libre('Poppins', 'sans'),
  libre('Roboto Condensed', 'sans'),
  libre('Roboto Mono', 'mono'),
  libre('IBM Plex Mono', 'mono'),
  libre('Courier Prime', 'mono'),
  libre('Great Vibes', 'manuscrita', 'r'),
  libre('Pinyon Script', 'manuscrita', 'r'),
  libre('Dancing Script', 'manuscrita', 'rn'),
];

const porTitulo = (a: FuenteEditor, b: FuenteEditor) => a.titulo.localeCompare(b.titulo, 'en', { sensitivity: 'base' });

/**
 * Menú de letra del editor. TinyMCE no admite separadores en
 * font_family_formats, así que van las de Office primero y luego las libres,
 * cada grupo por orden alfabético.
 */
export const FUENTES_EDITOR: readonly FuenteEditor[] = [...FUENTES_OFFICE, ...[...FUENTES_LIBRES].sort(porTitulo)];

/** Grupos con rótulo (Formatos y «Aplicar a todo»): Office, serifa, sin serifa, monoespaciada y manuscrita. */
export const GRUPOS_FUENTES: ReadonlyArray<{ grupo: GrupoFuente; rotulo: string; fuentes: readonly FuenteEditor[] }> = (
  [['office', 'Office'], ['serifa', 'Serif'], ['sans', 'Sans serif'], ['mono', 'Monospace'], ['manuscrita', 'Script']] as const
).map(([grupo, rotulo]) => ({ grupo, rotulo, fuentes: FUENTES_EDITOR.filter((f) => f.grupo === grupo) }));

/** Pila de una fuente del selector por su nombre (sin distinguir mayúsculas), o null si no está. */
export function pilaDeFuente(titulo: string): string | null {
  const t = String(titulo ?? '').replace(/["']/g, '').trim().toLowerCase();
  return FUENTES_EDITOR.find((f) => f.titulo.toLowerCase() === t)?.pila ?? null;
}

/** `font_family_formats` de TinyMCE. */
export const FONT_FAMILY_FORMATS = FUENTES_EDITOR.map((f) => `${f.titulo}=${f.pila}`).join('; ');

export const TAMANOS_EDITOR = ['6pt', '7pt', '7.5pt', '8pt', '8.5pt', '9pt', '9.5pt', '10pt', '10.5pt', '11pt', '12pt', '14pt', '16pt', '18pt', '20pt', '24pt', '28pt', '36pt'] as const;

/** `font_size_formats` de TinyMCE. */
export const FONT_SIZE_FORMATS = TAMANOS_EDITOR.join(' ');

/** Archivos de public/fonts que el editor sirve: las gemelas bajo el nombre de Office y las libres bajo el suyo. */
export const FUENTES_WEB: ReadonlyArray<{ familia: string; archivo: string; negrita: boolean; cursiva: boolean; libre: boolean }> = FUENTES_EDITOR.flatMap(
  (f) => (f.web ?? []).map((archivo, i) => ({ familia: f.titulo, archivo, negrita: i % 2 === 1, cursiva: i >= 2, libre: f.grupo !== 'office' })),
);

const FORMATO_FUENTE: Record<string, string> = { woff2: 'woff2', otf: 'opentype', ttf: 'truetype' };

const fontFace = (f: (typeof FUENTES_WEB)[number]) =>
  `@font-face { font-family: '${f.familia}'; src: url('/fonts/${f.archivo}') format('${FORMATO_FUENTE[f.archivo.split('.').pop() ?? ''] ?? 'truetype'}'); `
  + `font-weight: ${f.negrita ? 'bold' : 'normal'}; font-style: ${f.cursiva ? 'italic' : 'normal'}; font-display: swap; }`;

/**
 * @font-face de las fuentes del editor para su content_style. Son los mismos
 * archivos que usa processing-ms, así que el editor corta los renglones donde
 * los corta el PDF. Si un archivo no llega, el navegador sigue con la
 * siguiente fuente de la pila. Declararlas no descarga nada: cada archivo se
 * pide la primera vez que un texto lo usa.
 */
export function cssFuentesEquivalentes(): string {
  return `${FUENTES_WEB.map(fontFace).join('\n')}\n${cssNotoCjk()}`;
}

/**
 * Solo las libres, para la página que rodea al editor: el menú de letra de
 * TinyMCE y la vista previa de Formatos pintan cada nombre con su letra. Las de
 * Office NO van aquí: un @font-face 'Arial' cambiaría la letra de la interfaz.
 */
export function cssFuentesLibres(): string {
  return FUENTES_WEB.filter((f) => f.libre).map(fontFace).join('\n');
}

const ID_ESTILO_LIBRES = 'ez-fuentes-libres';

/** Pone (una vez) las @font-face de las libres en `doc`. Un fallo solo deja el menú con la letra del sistema. */
export function instalarFuentesLibres(doc: Document | null = typeof document === 'undefined' ? null : document): void {
  try {
    if (!doc?.head || doc.getElementById(ID_ESTILO_LIBRES)) return;
    const estilo = doc.createElement('style');
    estilo.id = ID_ESTILO_LIBRES;
    estilo.textContent = cssFuentesLibres();
    doc.head.appendChild(estilo);
  } catch {
    /* sin las @font-face cada nombre sale con la letra del sistema */
  }
}

/**
 * Estilos de borde de celda que se ofrecen: los que el Word también sabe
 * pintar. Groove, ridge, inset y outset salían distinto (o planos) en el DOCX.
 */
export const ESTILOS_BORDE_TABLA = [
  { title: 'Solid', value: 'solid' },
  { title: 'Dotted', value: 'dotted' },
  { title: 'Dashed', value: 'dashed' },
  { title: 'Double', value: 'double' },
  { title: 'None', value: 'none' },
];

/** Botones de tabla del plugin gratuito, en la barra flotante que sale al estar dentro de una tabla. */
export const BARRA_TABLA = 'tableprops tabledelete | tableinsertrowbefore tableinsertrowafter tabledeleterow | '
  + 'tableinsertcolbefore tableinsertcolafter tabledeletecol | tablemergecells tablesplitcells | '
  + 'tablecellprops tablerowprops | tablecellbackgroundcolor tablecellbordercolor tablecellborderwidth tablecellborderstyle tablecellvalign';

/**
 * Qué se redimensiona arrastrando en Revisión. Imágenes en ancho y alto (esquina =
 * proporcional, lados = libre), y tablas: TinyMCE 8 solo pone las barras para
 * arrastrar el borde de una columna o de una fila si 'table' está aquí, aunque
 * table_resize_bars siga encendido. Con 'img' a secas las columnas no se movían.
 */
export const REDIMENSIONAR_OBJETOS = 'img,table';

/**
 * Barra flotante de tabla plegable con un ojito (pedido de Andrey, 6 oct 2026):
 * abierta tapa el texto que queda encima de la tabla. Sale PLEGADA por defecto
 * (un botón pequeño «Opciones de tabla» para abrirla) y abierta lleva 👁 al
 * final para plegarla. La elección se recuerda en el navegador. Reemplaza a la barra del plugin
 * (table_toolbar: '' en el editor).
 */
export const BOTON_PLEGAR_TABLA = 'ezplegartabla';
export const BOTON_ABRIR_TABLA = 'ezabrirtabla';
const CLAVE_BARRA_TABLA_PLEGADA = 'ez.editor.barraTablaPlegada';
const ICONO_OJO = '<svg width="24" height="24" viewBox="0 0 24 24"><path d="M12 5C6.5 5 2.7 9.4 1.6 11.4a1.2 1.2 0 0 0 0 1.2C2.7 14.6 6.5 19 12 19s9.3-4.4 10.4-6.4a1.2 1.2 0 0 0 0-1.2C21.3 9.4 17.5 5 12 5Zm0 11.5a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9Zm0-2.2a2.3 2.3 0 1 0 0-4.6 2.3 2.3 0 0 0 0 4.6Z" fill-rule="evenodd"/></svg>';
const ICONO_OJO_TACHADO = '<svg width="24" height="24" viewBox="0 0 24 24"><path d="M3.3 2.3 2 3.6l3.2 3.2C3.4 8.2 2.2 10 1.6 11.4a1.2 1.2 0 0 0 0 1.2C2.7 14.6 6.5 19 12 19c1.9 0 3.6-.5 5-1.3l3.4 3.4 1.3-1.3L3.3 2.3Zm8.7 14.2a4.5 4.5 0 0 1-4.3-5.8l1.8 1.8a2.3 2.3 0 0 0 2.6 2.6l1.8 1.8c-.6.4-1.2.6-1.9.6ZM12 5c-1.3 0-2.6.3-3.7.7l2 2c.6-.1 1.1-.2 1.7-.2a4.5 4.5 0 0 1 4.5 4.5c0 .6-.1 1.1-.2 1.7l3 3c1.6-1.3 2.7-2.8 3.1-3.6a1.2 1.2 0 0 0 0-1.2C21.3 9.4 17.5 5 12 5Z"/></svg>';

export function barraTablaPlegada(): boolean {
  try { return localStorage.getItem(CLAVE_BARRA_TABLA_PLEGADA) !== '0'; } catch { return true; }
}

export interface EditorConBarraTabla {
  getBody: () => HTMLElement;
  dom: { is: (el: Node, selector: string) => boolean; isEditable: (el: Node | null) => boolean };
  focus: () => void;
  nodeChanged: () => void;
  ui: { registry: {
    addIcon: (nombre: string, svg: string) => void;
    addButton: (nombre: string, spec: Record<string, unknown>) => void;
    addContextToolbar: (nombre: string, spec: Record<string, unknown>) => void;
  } };
}

export function registrarBarraTablaPlegable(editor: EditorConBarraTabla): void {
  let plegada = barraTablaPlegada();
  // El mismo criterio que la barra del plugin de tablas.
  const esTablaEditable = (n: Element) => editor.dom.is(n, 'table') && editor.getBody().contains(n) && editor.dom.isEditable(n.parentNode);
  const cambiar = (v: boolean) => {
    plegada = v;
    try { localStorage.setItem(CLAVE_BARRA_TABLA_PLEGADA, v ? '1' : '0'); } catch { /* sin almacenamiento: vale para esta sesión */ }
    // Con el foco en el editor, NodeChange vuelve a elegir la barra.
    editor.focus();
    editor.nodeChanged();
  };
  const r = editor.ui.registry;
  r.addIcon('ez-ojo', ICONO_OJO);
  r.addIcon('ez-ojo-tachado', ICONO_OJO_TACHADO);
  r.addButton(BOTON_PLEGAR_TABLA, { icon: 'ez-ojo-tachado', tooltip: 'Hide table options', onAction: () => cambiar(true) });
  r.addButton(BOTON_ABRIR_TABLA, { icon: 'ez-ojo', text: 'Table options', tooltip: 'Show table options', onAction: () => cambiar(false) });
  r.addContextToolbar('eztabla', { predicate: (n: Element) => !plegada && esTablaEditable(n), items: `${BARRA_TABLA} | ${BOTON_PLEGAR_TABLA}`, scope: 'node', position: 'node' });
  r.addContextToolbar('eztablaplegada', { predicate: (n: Element) => plegada && esTablaEditable(n), items: BOTON_ABRIR_TABLA, scope: 'node', position: 'node' });
}

export const BOTON_TODO_EL_DOCUMENTO = 'ezaplicartodo';
/** Menú de interlineado propio: el `lineheight` de TinyMCE no guarda el valor de Word. */
export const BOTON_INTERLINEADO = 'ezinterlineado';

/**
 * Barras del editor de Revisión. Con plantilla de formato (Opción 4) la letra
 * y los márgenes los fija la plantilla —el editor los impone con !important y
 * el export los vuelve a hornear—, así que ahí no se ofrecen «Aplicar a todo»
 * ni «Márgenes» (margenes-hoja).
 * Los botones de tabla van en BARRA_TABLA y no aquí: en paneles de 800-950 px
 * la segunda fila se partía en tres y el documento perdía un renglón de alto.
 */
export function barrasEditorRevision(conPlantilla: boolean, conPaginacion = false): { toolbar1: string; toolbar2: string } {
  const todo = conPlantilla ? '' : ` ${BOTON_TODO_EL_DOCUMENTO}`;
  const margenes = conPlantilla ? '' : ` ${BOTON_MARGENES}`;
  // «Paginación» (paginacion-hojas): solo en la traducción, junto a «Márgenes».
  const paginacion = conPlantilla || !conPaginacion ? '' : ` ${BOTON_PAGINACION}`;
  return {
    toolbar1: `undo redo | fontfamily fontsize${todo} | bold italic underline strikethrough | forecolor backcolor | alignleft aligncenter alignright alignjustify`,
    toolbar2: `table | bullist numlist | outdent indent | ${BOTON_INTERLINEADO}${margenes}${paginacion} | superscript subscript | charmap hr | removeformat | searchreplace | preview fullscreen wordcount`,
  };
}

// ── Aplicar a todo el documento ─────────────────────────────────────────────

export interface CambioTipografia {
  /** Pila de font-family (la misma que escribe el selector). */
  familia?: string | null;
  /** Tamaño en puntos. */
  tamanoPt?: number | null;
}

export type FijarEstilo = (el: HTMLElement, propiedad: 'font-family' | 'font-size' | 'line-height', valor: string) => void;

// Zonas que no se tocan: el encabezado y el pie (se editan aparte, como en
// Word, y se dibujan como banda en el PDF), sus réplicas de solo lectura y
// los elementos internos de TinyMCE.
const ZONAS_EXCLUIDAS = '.doc-header, .doc-footer, .hf-ghost, [contenteditable="false"], [data-mce-bogus]';
// Fuentes de símbolos (casillas ☐☑, flechas, emoji): con otra letra el glifo
// queda en manos del respaldo de cada visor y el Word pinta otro símbolo.
const FUENTE_SIMBOLOS = /wingdings|webdings|dingbats|\bsymbol\b|emoji|ms gothic|ms mincho|marlett/i;
const SIN_LETRA = new Set(['IMG', 'BR', 'HR', 'svg', 'VIDEO', 'AUDIO', 'CANVAS']);
const MEDIDA_ABSOLUTA = /^\s*(\d+(?:\.\d+)?)\s*(pt|px)\s*$/i;
// Debajo de 4 pt no es texto sino un separador (renglón de 1 pt, celda
// colapsada): agrandarlo abriría huecos en el diseño.
const MINIMO_TEXTO_PT = 4;

const enPt = (valor: number, unidad: string) => (unidad.toLowerCase() === 'px' ? valor * 0.75 : valor);
const redondear = (n: number) => Math.round(n * 100) / 100;

const fijarPorDefecto: FijarEstilo = (el, propiedad, valor) => el.style.setProperty(propiedad, valor);

/**
 * Aplica la fuente y/o el tamaño a todo el contenido de `body` y devuelve
 * cuántos elementos cambió. Solo toca font-family, font-size y (si era
 * absoluto) el line-height que acompaña al tamaño: la estructura, las tablas y
 * los marcadores quedan como estaban.
 *
 * - Cada hoja (`.manuscript-page`; sin hojas, cada bloque de primer nivel)
 *   recibe la fuente y el tamaño, y lo que no declara letra la hereda.
 * - Lo que ya declara su propia fuente o tamaño se reescribe. Los tamaños
 *   relativos (%, em) se respetan: las notas al 70 % siguen siendo más
 *   pequeñas que su renglón.
 *
 * `fijar` debe ser `editor.dom.setStyle` en TinyMCE: escribir en el style a
 * pelo deja desfasado el data-mce-style y lo serializado sería el valor viejo.
 */
export function aplicarATodoElDocumento(body: HTMLElement, cambio: CambioTipografia, fijar: FijarEstilo = fijarPorDefecto): number {
  const familia = (cambio.familia || '').trim();
  const tamanoPt = cambio.tamanoPt && Number.isFinite(cambio.tamanoPt) && cambio.tamanoPt > 0 ? redondear(cambio.tamanoPt) : null;
  if (!body || (!familia && !tamanoPt)) return 0;

  const excluido = (el: Element) => !!el.closest(ZONAS_EXCLUIDAS);
  let hojas = Array.from(body.querySelectorAll<HTMLElement>('.manuscript-page')).filter((el) => !excluido(el));
  if (!hojas.length) {
    hojas = Array.from(body.children).filter((el): el is HTMLElement => !SIN_LETRA.has(el.tagName) && !excluido(el));
  }
  const esHoja = new Set<Element>(hojas);
  const cambiados = new Set<HTMLElement>();

  const cambiarTamano = (el: HTMLElement, forzar: boolean) => {
    if (!tamanoPt) return;
    const actual = MEDIDA_ABSOLUTA.exec(el.style.fontSize || '');
    const ptActual = actual ? enPt(parseFloat(actual[1]), actual[2]) : null;
    if (!forzar && (ptActual === null || ptActual < MINIMO_TEXTO_PT)) return;
    if (ptActual !== null && Math.abs(ptActual - tamanoPt) < 0.01) return;
    fijar(el, 'font-size', `${tamanoPt}pt`);
    cambiados.add(el);
    // Un interlineado absoluto se escala con la letra: si no, al agrandar
    // los renglones se montarían unos sobre otros.
    const lh = MEDIDA_ABSOLUTA.exec(el.style.lineHeight || '');
    if (lh && ptActual && ptActual >= MINIMO_TEXTO_PT) {
      fijar(el, 'line-height', `${redondear(enPt(parseFloat(lh[1]), lh[2]) * (tamanoPt / ptActual))}pt`);
    }
  };

  const cambiarFamilia = (el: HTMLElement, forzar: boolean) => {
    if (!familia) return;
    const actual = el.style.fontFamily || '';
    if (!forzar && !actual) return;
    if (FUENTE_SIMBOLOS.test(actual)) return;
    if (normalizarPila(actual) === normalizarPila(familia)) return;
    fijar(el, 'font-family', familia);
    cambiados.add(el);
  };

  for (const hoja of hojas) {
    cambiarFamilia(hoja, true);
    cambiarTamano(hoja, true);
  }
  for (const el of Array.from(body.querySelectorAll<HTMLElement>('[style]'))) {
    if (esHoja.has(el) || excluido(el)) continue;
    cambiarFamilia(el, false);
    cambiarTamano(el, false);
  }
  return cambiados.size;
}

const normalizarPila = (pila: string) => pila.split(',').map((f) => f.trim().replace(/^['"]|['"]$/g, '').toLowerCase()).filter(Boolean).join(',');

/** Fuente y tamaño que declara la primera hoja (para marcar la opción actual en el menú). */
export function tipografiaDeLaHoja(body: HTMLElement | null | undefined): { familia: string | null; tamanoPt: number | null } {
  const hoja = body?.querySelector<HTMLElement>('.manuscript-page') ?? null;
  const fam = hoja?.style.fontFamily ? normalizarPila(hoja.style.fontFamily).split(',')[0] : null;
  const m = MEDIDA_ABSOLUTA.exec(hoja?.style.fontSize || '');
  return { familia: fam || null, tamanoPt: m ? redondear(enPt(parseFloat(m[1]), m[2])) : null };
}

// ── Interlineado ────────────────────────────────────────────────────────────

/** Valores del menú: los de Word (Múltiple) más 0,5. Significan lo mismo que en Word: 1,0 es su «Sencillo». */
export const INTERLINEADOS_EDITOR = [0.5, 1, 1.15, 1.5, 2, 2.5, 3] as const;

/**
 * Word cuenta «Múltiple N» sobre la línea natural de la fuente (1,15 del cuerpo
 * en Arial, Helvetica y Times New Roman) y CSS cuenta line-height sobre el
 * cuerpo. Por eso el bloque lleva line-height = N × 1,15 —así el editor y el
 * PDF tienen el paso de Word— y N en data-ez-interlineado, que es lo que el
 * backend escribe en el .docx (w:line = N × 240, lineRule auto) sin pasar por
 * los recortes de paridad.
 */
export const FACTOR_LINEA_WORD = 1.15;
export const ATRIBUTO_INTERLINEADO = 'data-ez-interlineado';
const MINIMO_INTERLINEADO = 0.06;
const MAXIMO_INTERLINEADO = 5;

/**
 * «Sencillo» de cada letra, en em, el de Google Docs (decisión de Andrey del
 * 16 oct: MANDA Docs). En las de Office coincide con el de Word (GDI con las
 * métricas de Windows); en las libres, el de Docs. Así «1,0» es en el editor
 * el «Sencillo» de esa letra en Docs, y el Word lleva ese mismo «Múltiple»
 * (el backend calibra w:line con la misma tabla), que Docs pinta igual. Las de Office, de los TTF de Microsoft (o de
 * sus gemelas métricas); Helvetica, la Arial con que la sustituye Word. Las
 * libres, las del catálogo del backend (fuentes-libres-word.util). Una letra
 * que no está (las CJK) usa la de Arial.
 */
export const LINEA_NATURAL_WORD: Readonly<Record<string, number>> = {
  // Arial, Arial Narrow, Helvetica y Times dan 1,1475-1,1499: se quedan en el 1,15 de siempre (±0,2 %).
  calibri: 1.2207, carlito: 1.2207, cambria: 1.1724, 'courier new': 1.1328, courier: 1.1328, georgia: 1.1362, gelasio: 1.1362,
  // Libres: la línea natural de Google Docs (y de OnlyOffice y del navegador): typo si la letra pide
  // USE_TYPO_METRICS, si no la de Windows. Medido en el Docs de Andrey el 16 oct: Lora 1,27, Montserrat
  // 1,21, Roboto = Word. MANDA Docs: en Microsoft Word, las que no coinciden salen más altas (Lora +17 %).
  lora: 1.28, 'eb garamond': 1.305, 'libre baskerville': 1.24, 'source serif 4': 1.371, 'noto serif': 1.362, 'pt serif': 1.325,
  spectral: 1.522, 'crimson pro': 1.1113, 'roboto serif': 1.171, 'ibm plex serif': 1.436, roboto: 1.2002, 'open sans': 1.3618,
  montserrat: 1.219, 'source sans 3': 1.424, 'pt sans': 1.294, 'ibm plex sans': 1.3, 'fira sans': 1.2, 'libre franklin': 1.212,
  poppins: 1.5, 'roboto condensed': 1.2002, 'roboto mono': 1.3188, 'ibm plex mono': 1.3, 'courier prime': 1.123,
  'great vibes': 1.252, 'pinyon script': 1.2476, 'dancing script': 1.2,
  // CJK: las Noto con la línea natural de Docs (medida en su Docs con bloques de 20 renglones).
  'noto sans kr': 1.876, 'noto sans jp': 1.867, 'noto sans sc': 1.867, 'noto sans tc': 1.867,
};

const RE_HANGUL = /[\u1100-\u11ff\u3130-\u318f\uac00-\ud7af]/;
const RE_KANA = /[\u3040-\u30ff\u31f0-\u31ff]/;
const RE_HAN = /[\u3400-\u4dbf\u4e00-\u9fff\uf900-\ufaff]/;

/** La línea natural de Docs del texto CJK (la de su Noto), o 0 sin texto CJK. */
export function lineaCjkDeTexto(texto: string): number {
  if (RE_HANGUL.test(texto)) return LINEA_NATURAL_WORD['noto sans kr'];
  if (RE_KANA.test(texto)) return LINEA_NATURAL_WORD['noto sans jp'];
  return RE_HAN.test(texto) ? LINEA_NATURAL_WORD['noto sans sc'] : 0;
}

/** La línea natural de Word de una pila de font-family (la primera familia manda). */
export function lineaNaturalDeFamilia(pila: string | null | undefined): number {
  const primera = (pila || '').split(',')[0].replace(/["']/g, '').replace(/\s+/g, ' ').trim().toLowerCase();
  return LINEA_NATURAL_WORD[primera] ?? FACTOR_LINEA_WORD;
}

/**
 * La línea natural de la letra con que pinta el bloque (la calculada; sin
 * ventana, la declarada en él o en su antepasado). Con texto CJK manda la mayor
 * de las dos (la Noto de su escritura o la del bloque): es la que fija el
 * renglón en Docs y en Word, y la que usa el backend.
 */
export function lineaNaturalDe(el: Element): number {
  const cjk = lineaCjkDeTexto(el.textContent ?? '');
  return Math.max(cjk, lineaNaturalDeLaLetra(el));
}

function lineaNaturalDeLaLetra(el: Element): number {
  let pila = '';
  try {
    const vista = el.ownerDocument?.defaultView;
    pila = vista?.getComputedStyle(el).fontFamily ?? '';
  } catch { /* sin estilos calculados */ }
  if (!pila) pila = (el.closest('[style*="font-family"]') as HTMLElement | null)?.style.fontFamily ?? '';
  return lineaNaturalDeFamilia(pila);
}

/** line-height (sin unidad) que lleva un bloque con interlineado de Word `m` en una letra de línea natural `linea`: 1,15 en Arial → '1.3225'. */
export function lineHeightDeInterlineado(m: number, linea: number = FACTOR_LINEA_WORD): string {
  return String(Math.round(m * linea * 10000) / 10000);
}

/** `line_height_formats` de TinyMCE (lo usaría su menú nativo, que la barra ya no lleva). */
export const LINE_HEIGHT_FORMATS = INTERLINEADOS_EDITOR.map((m) => lineHeightDeInterlineado(m)).join(' ');

/**
 * Texto del menú con punto ('1.0', '1.15'), que es el inglés; TINYMCE_TEXTOS lo
 * pone con coma en los demás idiomas.
 */
export function etiquetaInterlineado(m: number): string {
  return m.toFixed(Math.max(1, (String(m).split('.')[1] ?? '').length));
}

/** «Personalizado…»: lo que se puede escribir, con dos decimales como mucho. */
export const INTERLINEADO_PERSONALIZADO_MIN = 0.5;
export const INTERLINEADO_PERSONALIZADO_MAX = 5;

/**
 * El valor escrito en «Personalizado…» («0,8», «1.3», « 2 »), redondeado a dos
 * decimales, o null si no es un número entre 0,5 y 5.
 */
export function leerInterlineadoPersonalizado(texto: string | null | undefined): number | null {
  const m = /^\s*(\d{1,2}(?:[.,]\d*)?|[.,]\d+)\s*$/.exec(texto ?? '');
  if (!m) return null;
  const v = Math.round(parseFloat(m[1].replace(',', '.')) * 100) / 100;
  return Number.isFinite(v) && v >= INTERLINEADO_PERSONALIZADO_MIN && v <= INTERLINEADO_PERSONALIZADO_MAX ? v : null;
}

/** Interlineado de Word que declara el bloque, o null si no lo eligió nadie. */
export function interlineadoDe(el: Element | null | undefined): number | null {
  const v = parseFloat(el?.getAttribute(ATRIBUTO_INTERLINEADO) ?? '');
  return Number.isFinite(v) && v >= MINIMO_INTERLINEADO && v <= MAXIMO_INTERLINEADO ? v : null;
}

// Los mismos bloques que el lineheight de TinyMCE.
const SELECTOR_BLOQUE = 'p,h1,h2,h3,h4,h5,h6,li,td,th,div';
const CONTENEDOR = /^(P|H[1-6]|LI|TD|TH|DIV|TABLE|THEAD|TBODY|TFOOT|TR|UL|OL|DL|BLOCKQUOTE|SECTION|ARTICLE|HEADER|FOOTER|FIGURE|PRE|HR)$/;

// La hoja o la celda que solo envuelve párrafos no se marca: se marcan sus
// párrafos, que son los que el Word convierte en w:p. Si además tiene texto
// suelto, también ella (ese texto es otro párrafo en el Word).
function esBloqueDeTexto(el: Element): boolean {
  if (!el.querySelector(SELECTOR_BLOQUE)) return true;
  return Array.from(el.childNodes).some((n) => (n.nodeType === 3
    ? /\S/.test(n.nodeValue || '')
    : n.nodeType === 1 && !CONTENEDOR.test((n as Element).tagName.toUpperCase()) && /\S/.test(n.textContent || '')));
}

function esSeparador(el: HTMLElement): boolean {
  const m = MEDIDA_ABSOLUTA.exec(el.style?.fontSize || '');
  return !!m && enPt(parseFloat(m[1]), m[2]) < MINIMO_TEXTO_PT;
}

/**
 * De una lista de candidatos, los bloques que reciben el interlineado: los de
 * texto, fuera del encabezado, el pie y lo bloqueado, y sin los separadores de
 * menos de 4 pt (agrandarlos abriría huecos en el diseño).
 */
export function bloquesParaInterlineado(candidatos: Iterable<Element>): HTMLElement[] {
  const elegidos = new Set<HTMLElement>();
  for (const el of Array.from(candidatos)) {
    // Sin instanceof: los nodos del editor son de otra ventana (el iframe).
    if (el?.nodeType !== 1 || !el.matches(SELECTOR_BLOQUE)) continue;
    if (el.closest(ZONAS_EXCLUIDAS) || esSeparador(el as HTMLElement) || !esBloqueDeTexto(el)) continue;
    elegidos.add(el as HTMLElement);
  }
  return Array.from(elegidos);
}

/** Bloques de todo el documento que reciben el interlineado. */
export function bloquesDelDocumento(body: HTMLElement): HTMLElement[] {
  return bloquesParaInterlineado(body.querySelectorAll(SELECTOR_BLOQUE));
}

/** El interlineado común a todos los bloques, o null si no hay o difieren. */
export function interlineadoComun(bloques: readonly Element[]): number | null {
  if (!bloques.length) return null;
  const primero = interlineadoDe(bloques[0]);
  return primero !== null && bloques.every((b) => interlineadoDe(b) === primero) ? primero : null;
}

/** Lo que el interlineado necesita del editor: en TinyMCE, `editor.dom`. */
export interface DomInterlineado {
  setStyle: (el: HTMLElement, propiedad: string, valor: string) => void;
  setAttrib: (el: HTMLElement, nombre: string, valor: string | null) => void;
}

const domPorDefecto: DomInterlineado = {
  setStyle: (el, p, v) => el.style.setProperty(p, v),
  setAttrib: (el, n, v) => (v === null ? el.removeAttribute(n) : el.setAttribute(n, v)),
};

// El de su letra o el de siempre (M × 1,15: lo que guardaban los documentos de antes del 16 oct).
const lineHeightCuadra = (el: HTMLElement, m: number) => {
  const lh = parseFloat(el.style?.lineHeight || '');
  return Math.abs(lh - m * FACTOR_LINEA_WORD) < 0.001 || Math.abs(lh - parseFloat(lineHeightDeInterlineado(m, lineaNaturalDe(el)))) < 0.001;
};

/**
 * Pone el interlineado de Word `m` en los bloques: line-height = m × la línea
 * natural de su letra (1,15 en Arial) y la marca data-ez-interlineado.
 * Devuelve cuántos cambió.
 */
export function aplicarInterlineado(bloques: Iterable<HTMLElement>, m: number, dom: DomInterlineado = domPorDefecto): number {
  if (!Number.isFinite(m) || m < MINIMO_INTERLINEADO || m > MAXIMO_INTERLINEADO) return 0;
  let n = 0;
  for (const el of Array.from(bloques)) {
    const lh = lineHeightDeInterlineado(m, lineaNaturalDe(el));
    if (interlineadoDe(el) === m && el.style?.lineHeight === lh) continue;
    dom.setStyle(el, 'line-height', lh);
    dom.setAttrib(el, ATRIBUTO_INTERLINEADO, String(m));
    n++;
  }
  return n;
}

/**
 * Quita la marca de los bloques cuyo line-height ya no es el de su
 * interlineado («Borrar formato» se lleva el style y deja el atributo): el
 * Word no debe salir con un interlineado que el editor y el PDF ya no tienen.
 */
export function sanearInterlineados(body: HTMLElement, dom: DomInterlineado = domPorDefecto): number {
  let n = 0;
  for (const el of Array.from(body.querySelectorAll<HTMLElement>(`[${ATRIBUTO_INTERLINEADO}]`))) {
    const m = interlineadoDe(el);
    if (m !== null && lineHeightCuadra(el, m)) continue;
    dom.setAttrib(el, ATRIBUTO_INTERLINEADO, null);
    n++;
  }
  return n;
}

/** Lo que el diálogo de «Personalizado…» usa de TinyMCE (`editor.windowManager`). */
export interface VentanasEditor {
  open: (spec: Record<string, unknown>) => unknown;
}

const NOMBRE_CAMPO_PERSONALIZADO = 'interlineado';

/** `editor.translate` sin perder su `this`; sin él, el texto tal cual. */
const traducirCon = (editor: { translate?: (texto: string) => string }) => (texto: string) => {
  try { return editor.translate?.(texto) || texto; } catch { return texto; }
};
const BOTON_APLICAR_PERSONALIZADO = 'aplicar';

/**
 * El número con la coma de los idiomas del panel que la usan: TinyMCE traduce
 * «1.15» a «1,15» (TINYMCE_TEXTOS), y de ahí se saca el separador.
 */
function conSeparador(m: number, traducir?: (t: string) => string): string {
  const texto = etiquetaInterlineado(m);
  let coma = false;
  try { coma = (traducir?.('1.15') ?? '1.15').includes(','); } catch { /* con punto */ }
  return coma ? texto.replace('.', ',') : texto;
}

/**
 * Abre «Personalizado…»: un campo para escribir el interlineado (0,5 a 5, dos
 * decimales) que se aplica igual que una opción del menú. Sin ventana (pruebas,
 * editor a medio montar) no hace nada.
 */
export function abrirInterlineadoPersonalizado(
  ventanas: VentanasEditor | undefined,
  actual: number | null,
  aplicar: (m: number) => void,
  traducir?: (t: string) => string,
): void {
  if (!ventanas?.open) return;
  const t = (s: string) => { try { return traducir?.(s) || s; } catch { return s; } };
  const valido = (datos: Record<string, string>) => leerInterlineadoPersonalizado(datos?.[NOMBRE_CAMPO_PERSONALIZADO]) !== null;
  ventanas.open({
    title: t('Custom line height'),
    size: 'normal',
    body: {
      type: 'panel',
      items: [
        { type: 'input', name: NOMBRE_CAMPO_PERSONALIZADO, label: t('Line height (0.5 to 5)'), inputMode: 'decimal', maximized: false },
        { type: 'htmlpanel', html: `<p style="margin:4px 0 0;color:#6b7280;font-size:12px">${t('1.0 is single spacing, as in Word and Google Docs.')}</p>` },
      ],
    },
    initialData: { [NOMBRE_CAMPO_PERSONALIZADO]: actual !== null ? conSeparador(actual, traducir) : '' },
    buttons: [
      { type: 'cancel', text: t('Cancel') },
      { type: 'submit', name: BOTON_APLICAR_PERSONALIZADO, text: t('Apply'), primary: true, enabled: actual !== null },
    ],
    onChange: (api: { getData: () => Record<string, string>; setEnabled: (n: string, v: boolean) => void }) => {
      try { api.setEnabled(BOTON_APLICAR_PERSONALIZADO, valido(api.getData())); } catch { /* el botón se queda como está */ }
    },
    onSubmit: (api: { getData: () => Record<string, string>; close: () => void }) => {
      const m = leerInterlineadoPersonalizado(api.getData()?.[NOMBRE_CAMPO_PERSONALIZADO]);
      if (m === null) return;
      api.close();
      aplicar(m);
    },
  });
}

const opcionesInterlineado = (
  actual: number | null,
  aplicar: (m: number) => void,
  personalizado?: () => void,
  traducir?: (t: string) => string,
) => {
  const fijas: Array<Record<string, unknown>> = INTERLINEADOS_EDITOR.map((m) => ({
    type: 'togglemenuitem',
    text: etiquetaInterlineado(m),
    active: actual === m,
    onAction: () => aplicar(m),
  }));
  if (!personalizado) return fijas;
  // Un valor escrito a mano sale marcado debajo de los de siempre.
  const propio = actual !== null && !(INTERLINEADOS_EDITOR as readonly number[]).includes(actual)
    ? [{ type: 'togglemenuitem', text: conSeparador(actual, traducir), active: true, onAction: () => aplicar(actual) }]
    : [];
  return [...fijas, ...propio, { type: 'separator' }, { type: 'menuitem', text: 'Custom...', onAction: personalizado }];
};

/** Lo mínimo del editor de TinyMCE que usa el menú de interlineado. */
export interface EditorConInterlineado {
  getBody: () => HTMLElement;
  dom: DomInterlineado;
  selection: { getSelectedBlocks: () => Element[] };
  undoManager: { transact: (fn: () => void) => unknown };
  nodeChanged?: () => void;
  dispatch?: (nombre: string, args?: unknown) => unknown;
  on: (eventos: string, fn: (e: { command?: string }) => void) => void;
  ui: { registry: { addMenuButton: (nombre: string, spec: Record<string, unknown>) => void } };
  /** Para «Personalizado…». */
  windowManager?: VentanasEditor;
  translate?: (texto: string) => string;
}

/** Bloques de la selección; con varias celdas seleccionadas, las celdas y sus párrafos. */
export function bloquesDeLaSeleccion(editor: Pick<EditorConInterlineado, 'getBody' | 'selection'>): HTMLElement[] {
  const celdas = Array.from(editor.getBody().querySelectorAll('td[data-mce-selected],th[data-mce-selected]'));
  const candidatos = celdas.length
    ? celdas.flatMap((c) => [c, ...Array.from(c.querySelectorAll(SELECTOR_BLOQUE))])
    : editor.selection.getSelectedBlocks();
  return bloquesParaInterlineado(candidatos);
}

/**
 * Registra el menú de interlineado (en lugar del `lineheight` de TinyMCE, que
 * escribe el número tal cual y no guarda el valor de Word). Cada elección es
 * UN paso de deshacer.
 */
export function registrarBotonInterlineado(editor: EditorConInterlineado): void {
  // «Personalizado…» guarda los bloques al abrir la ventana: con ella delante
  // la selección del documento puede no ser la de antes.
  const aplicar = (m: number, elegidos?: HTMLElement[]) => {
    try {
      editor.undoManager.transact(() => {
        const bloques = elegidos?.filter((b) => b.isConnected) ?? bloquesDeLaSeleccion(editor);
        // El reflujo de la Opción 2 (reflujo-hojas) mide sus hojas antes y las
        // reacomoda después, dentro de este mismo paso de deshacer.
        editor.dispatch?.('EzCambioBloquesAntes', { bloques });
        if (aplicarInterlineado(bloques, m, editor.dom)) editor.dispatch?.('EzCambioBloques', { bloques });
      });
      editor.nodeChanged?.();
    } catch {
      /* un fallo aquí no debe tumbar el editor */
    }
  };
  editor.ui.registry.addMenuButton(BOTON_INTERLINEADO, {
    icon: 'line-height',
    tooltip: 'Line height',
    fetch: (callback: (items: unknown[]) => void) => {
      let actual: number | null = null;
      try { actual = interlineadoComun(bloquesDeLaSeleccion(editor)); } catch { /* sin opción marcada */ }
      const personalizado = () => {
        let bloques: HTMLElement[] = [];
        try { bloques = bloquesDeLaSeleccion(editor); } catch { /* sin selección */ }
        if (!bloques.length) return;
        abrirInterlineadoPersonalizado(editor.windowManager, actual, (m) => aplicar(m, bloques), traducirCon(editor));
      };
      callback(opcionesInterlineado(actual, (m) => aplicar(m), personalizado, traducirCon(editor)));
    },
  });
  // Se registra en setup, antes que el gestor de deshacer: lo que quita aquí
  // entra en el mismo paso que «Borrar formato».
  editor.on('ExecCommand', (e) => {
    if (!/^removeformat$/i.test(e?.command || '')) return;
    try { sanearInterlineados(editor.getBody(), editor.dom); } catch { /* el atributo sobrante lo ignora el editor */ }
  });
}

// ── Botón de TinyMCE ────────────────────────────────────────────────────────

/** Lo mínimo del editor de TinyMCE que usa el botón. */
export interface EditorConBarra {
  getBody: () => HTMLElement;
  dom: DomInterlineado;
  undoManager: { transact: (fn: () => void) => unknown };
  nodeChanged?: () => void;
  dispatch?: (nombre: string, args?: unknown) => unknown;
  /** `editor.translate`, para los rótulos de los grupos de letras. */
  translate?: (texto: string) => string;
  /** Para el interlineado «Personalizado…» de todo el documento. */
  windowManager?: VentanasEditor;
  ui: { registry: { addMenuButton: (nombre: string, spec: Record<string, unknown>) => void } };
}

/**
 * Registra el botón «Aplicar a todo el documento» (fuente, tamaño o
 * interlineado). Cada elección es UN paso de deshacer. `despues` corre tras
 * aplicar (p. ej. volver a medir los rellenos de guiones, que dependen de la letra).
 */
export function registrarBotonTodoElDocumento(editor: EditorConBarra, despues?: () => void): void {
  const aplicar = (cambiar: (body: HTMLElement) => number) => {
    try {
      let n = 0;
      editor.undoManager.transact(() => {
        // El reflujo de la Opción 2 (reflujo-hojas) mide cada hoja antes del
        // cambio y la reacomoda después, dentro de este mismo paso de deshacer.
        editor.dispatch?.('EzTodoElDocumentoAntes');
        n = cambiar(editor.getBody());
        if (n) editor.dispatch?.('EzTodoElDocumento');
      });
      editor.nodeChanged?.();
      if (n) despues?.();
    } catch {
      /* un fallo aquí no debe tumbar el editor (transact no revierte lo ya aplicado) */
    }
  };
  const tipografia = (cambio: CambioTipografia) => aplicar((body) => aplicarATodoElDocumento(body, cambio, (el, p, v) => editor.dom.setStyle(el, p, v)));
  const traducir = (texto: string) => {
    try { return editor.translate?.(texto) || texto; } catch { return texto; }
  };
  editor.ui.registry.addMenuButton(BOTON_TODO_EL_DOCUMENTO, {
    icon: 'typography',
    tooltip: 'Apply to the whole document',
    fetch: (callback: (items: unknown[]) => void) => {
      const actual = tipografiaDeLaHoja(editor.getBody());
      callback([
        {
          type: 'nestedmenuitem',
          text: 'Font for the whole document',
          // Por grupos con rótulo (el separador de TinyMCE no traduce su texto)
          // y cada nombre con su letra, como en el menú de letra.
          getSubmenuItems: () => GRUPOS_FUENTES.filter((g) => g.fuentes.length).flatMap((g) => [
            { type: 'separator', text: traducir(g.rotulo) },
            ...g.fuentes.map((f) => ({
              type: 'togglemenuitem',
              text: f.titulo,
              active: actual.familia === f.titulo.toLowerCase(),
              meta: { style: { tag: 'div', styles: { 'font-family': f.pila } } },
              onAction: () => tipografia({ familia: f.pila }),
            })),
          ]),
        },
        {
          type: 'nestedmenuitem',
          text: 'Size for the whole document',
          getSubmenuItems: () => TAMANOS_EDITOR.map((t) => ({
            type: 'togglemenuitem',
            text: t,
            active: actual.tamanoPt === parseFloat(t),
            onAction: () => tipografia({ tamanoPt: parseFloat(t) }),
          })),
        },
        {
          type: 'nestedmenuitem',
          text: 'Line height for the whole document',
          getSubmenuItems: () => {
            const actual = interlineadoComun(bloquesDelDocumento(editor.getBody()));
            const todo = (m: number) => aplicar((body) => aplicarInterlineado(bloquesDelDocumento(body), m, editor.dom));
            return opcionesInterlineado(actual, todo, () => abrirInterlineadoPersonalizado(editor.windowManager, actual, todo, traducir), traducir);
          },
        },
      ]);
    },
  });
}
